import type { LiveSummaryTag } from "@storytell/ui/live-summary";
import { type Component, For } from "solid-js";
import { useScene } from "~/hooks/useScene";
import { useI18n } from "~/i18n";
import { PeopleStack, SessionPill, SessionWindow, startedLabel } from "./internal/chief";
import { NotesLine, NotesPage, NotesTopic } from "./internal/notes";
import { MockScene, type Person } from "./internal/parts";
import { ScaledFrame } from "./ScaledFrame";

/** A point in the notes: it arrives at `at`, and its tag lands on it a moment later. */
export type SmartTagsNote = { id: string; topic: string; text: string; tagId: string; at: number };

export type SmartTagsData = {
  project: string;
  session: string;
  started: string;
  clock: string;
  people: Person[];
  tags: LiveSummaryTag[];
  /** The tag the notes are filtered to at the end. */
  focusTagId: string;
  notes: SmartTagsNote[];
};

const TAG_AFTER = 0.8;
const T = { filterCursor: 5.8, filter: 6.9 };

/**
 * Notes during a call, filling in and picking up their tags as it goes, then
 * filtered to one tag from the header's pills.
 */
export const SmartTagsMockup: Component<{ data: SmartTagsData; class?: string }> = (props) => {
  const { t, currentLanguage } = useI18n();
  const scene = useScene();
  const topics = () => [...new Set(props.data.notes.map((note) => note.topic))];
  const tagOf = (id: string) => props.data.tags.find((tag) => tag.id === id);
  const leaves = (tagId: string) => (tagId === props.data.focusTagId ? undefined : T.filter);
  return (
    <MockScene scene={scene} label={t("marketing.mock.tags.alt")} class={props.class}>
      <ScaledFrame chrome="app" width={820} height={540} narrow={{ width: 400, height: 700 }}>
        <SessionWindow
          project={props.data.project}
          title={props.data.session}
          facts={`${t("marketing.mock.live")} · ${startedLabel(props.data.started, currentLanguage())}`}
          live
          tab="notes"
          bar={<SessionPill clock={props.data.clock} people={<PeopleStack people={props.data.people} />} />}
        >
          <div class="notes-follow h-full">
            <NotesPage
              project={props.data.project}
              title={props.data.session}
              meta={startedLabel(props.data.started, currentLanguage())}
              total={props.data.notes.length}
              pills={props.data.tags.map((tag) => ({
                tag,
                count: props.data.notes.filter((note) => note.tagId === tag.id).length,
              }))}
              filter={{ tagId: props.data.focusTagId, at: T.filter, cursorAt: T.filterCursor }}
            >
              <For each={topics()}>
                {(topic) => {
                  const notes = () => props.data.notes.filter((note) => note.topic === topic);
                  return (
                    <>
                      <NotesTopic text={topic} appearAt={notes()[0]?.at} />
                      <For each={notes()}>
                        {(note) => (
                          <NotesLine
                            text={note.text}
                            tag={tagOf(note.tagId)}
                            appearAt={note.at}
                            tagAt={note.at + TAG_AFTER}
                            leaveAt={leaves(note.tagId)}
                          />
                        )}
                      </For>
                    </>
                  );
                }}
              </For>
            </NotesPage>
          </div>
        </SessionWindow>
      </ScaledFrame>
    </MockScene>
  );
};
